/**
 * What a board's grid draws and what a move or resize saves (2026-10-30). Pure.
 *
 * A board went blank while a widget was dragged in edit mode (React #185, maximum update depth). The board saved its
 * layout from react-grid-layout's onLayoutChange, which the grid also calls on mount and on every width or breakpoint
 * change, from its own effects. The save took the layout of the width on screen and wrote it as the board's 12-column
 * layout: at md (10 columns, widths cut down) and sm (one stacked column) that changed the board, the new layout went
 * back into the grid, and its effects answered again. Two changes stop that:
 *
 * - only a drop (onDragStop, onResizeStop) is saved, never onLayoutChange, so no save answers the grid's own effects,
 *   and every widget the drop moved is saved in one board update (withLayouts): one at a time, only the last stuck;
 * - lg and md draw the board's own columns (md only narrows them), so a drop there saves exactly what is on screen, and
 *   sm, which stacks the widgets, offers no moving at all: nothing drawn there can be read back as the board's layout.
 *
 * Editor review (2026-10-30): a widget is dragged from anywhere on it again, not only by the strip across its top (the
 * strip held the title and the buttons, so little of it moved the widget); its controls still do not start a drag. And
 * the board keeps its own columns down to 600 px: the settings panel beside it narrowed it under the old 996 px and the
 * board stacked, with no moving, as soon as a widget was picked.
 */
import { getBreakpointFromWidth } from "react-grid-layout";
import type { WidgetInstance, WidgetLayout } from "./types";

/** The grid's breakpoints: the narrowest width (px) each one draws from. Below md (600 px) the widgets are stacked. */
export const GRID_BREAKPOINTS = { lg: 1200, md: 600, sm: 0 } as const;

export type GridBreakpoint = keyof typeof GRID_BREAKPOINTS;

/** The breakpoint a grid of this width draws at, by react-grid-layout's own rule. */
export function gridBreakpoint(width: number): GridBreakpoint {
  return getBreakpointFromWidth(GRID_BREAKPOINTS, width) as GridBreakpoint;
}

/** Whether widgets can be moved and resized at a breakpoint: not at sm, where the grid stacks them. */
export function canArrange(breakpoint: GridBreakpoint): boolean {
  return breakpoint !== "sm";
}

/**
 * What does not start a drag: a control on a widget or in it (a button, a field, a link, a range chip, a table's pager),
 * so a press there does what the control does. Everything else on the widget moves it.
 */
export const DRAG_CANCEL = [
  ".dash-no-drag",
  "button",
  "input",
  "textarea",
  "select",
  "a[href]",
  '[role="slider"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[contenteditable="true"]',
].join(", ");

const DRAG = { handle: ".dash-drag-handle", cancel: DRAG_CANCEL };
const DRAG_ON = { enabled: true, ...DRAG };
const DRAG_OFF = { enabled: false, ...DRAG };
const RESIZE_ON = { enabled: true };
const RESIZE_OFF = { enabled: false };

/**
 * How a widget is dragged (react-grid-layout 2's dragConfig): from anywhere on it (its card is the .dash-drag-handle)
 * while the board is being arranged, never from a control (DRAG_CANCEL), and not at all otherwise. A press moves it only
 * after 3 px (the grid's threshold), so a click still picks it. One object for each case, so the grid is handed the same
 * one on every render.
 */
export function gridDragConfig(arrange: boolean): { enabled: boolean; handle: string; cancel: string } {
  return arrange ? DRAG_ON : DRAG_OFF;
}

/** Whether a widget can be resized (react-grid-layout 2's resizeConfig): only while the board is being arranged. */
export function gridResizeConfig(arrange: boolean): { enabled: boolean } {
  return arrange ? RESIZE_ON : RESIZE_OFF;
}

/** One widget's place as the grid reports it. */
export interface GridItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The grid's columns at each breakpoint: the board's own at lg and md, 6 at sm. */
export function gridCols(cols: number): Record<GridBreakpoint, number> {
  return { lg: cols, md: cols, sm: 6 };
}

const finite = (v: unknown, fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

/**
 * A widget's place as numbers the grid can lay out. A saved board is a blob the server keeps as it was sent, so a widget
 * can come with no layout or a part of one: the grid then threw before any widget's error boundary (a blank page), or,
 * with two such widgets, never finished its first layout (its compaction does not settle on NaN) and the tab hung with
 * no way to reach the editor. A missing field takes a default; a missing row goes under the widgets that have one.
 */
export function placeOf(layout: Partial<WidgetLayout> | undefined, bottom: number): WidgetLayout {
  return {
    x: Math.max(0, finite(layout?.x, 0)),
    y: Math.max(0, finite(layout?.y, bottom)),
    w: Math.max(1, finite(layout?.w, 4)),
    h: Math.max(1, finite(layout?.h, 4)),
  };
}

/** The row under every widget whose place is whole: where a widget with no row goes. */
export function bottomOf(widgets: readonly Pick<WidgetInstance, "layout">[]): number {
  return widgets.reduce((m, w) => Math.max(m, finite(w.layout?.y, 0) + finite(w.layout?.h, 0)), 0);
}

/** The grid's layouts: the board's own at lg and md (md only narrows the columns), the widgets stacked at sm. */
export function gridLayouts(widgets: readonly WidgetInstance[]): Record<GridBreakpoint, GridItem[]> {
  const bottom = bottomOf(widgets);
  const own = widgets.map((w) => ({ i: w.id, ...placeOf(w.layout, bottom) }));
  return {
    lg: own,
    md: own,
    sm: own.map((g, i) => ({ i: g.i, x: 0, y: i * g.h, w: 6, h: g.h })),
  };
}

/** One widget's new place, as a drop saves it. */
export interface LayoutWrite {
  id: string;
  layout: WidgetLayout;
}

/**
 * The layouts a drop saves: each widget the grid moved or resized, at a breakpoint that draws the board's own columns.
 * Nothing at sm. A layout the board already holds saves nothing, so saving what this returns and drawing the board
 * again gives nothing more to save.
 */
export function layoutWrites(
  widgets: readonly WidgetInstance[],
  layout: readonly GridItem[],
  breakpoint: GridBreakpoint,
): LayoutWrite[] {
  if (!canArrange(breakpoint)) return [];
  const out: LayoutWrite[] = [];
  for (const item of layout) {
    const w = widgets.find((x) => x.id === item.i);
    if (!w) continue;
    const { x, y, w: width, h } = item;
    if (w.layout?.x !== x || w.layout?.y !== y || w.layout?.w !== width || w.layout?.h !== h) {
      out.push({ id: w.id, layout: { x, y, w: width, h } });
    }
  }
  return out;
}

/**
 * The widgets with a drop's layouts in place, all of them at once. A drop that pushes other widgets moves several: the
 * store saved them one widget at a time, each from the board as it was at the last render, so only the last one stuck
 * and the board then disagreed with the grid. Pure.
 */
export function withLayouts(widgets: readonly WidgetInstance[], writes: readonly LayoutWrite[]): WidgetInstance[] {
  const byId = new Map(writes.map((x) => [x.id, x.layout]));
  return widgets.map((w) => {
    const layout = byId.get(w.id);
    return layout ? { ...w, layout } : w;
  });
}
